/**
 * User Action Logger Service
 * Logs user actions throughout the application via LogManagement API
 * Supports login, logout, CRUD operations, and navigation actions
 */

import { apiService } from './api';
import { UserActionLogDto, CreateUserActionLogDto } from '../types/logManagement';
import { UserActionLoggerDiagnostic } from '../utils/userActionLoggerDiagnostic';
import { loggingHealthService } from './loggingHealthService';

export interface UserAction {
  type: string;
  resourceType: string;
  resourceId?: string | undefined;
  userId?: string | undefined;
  sessionId?: string | undefined;
  ipAddress?: string | undefined;
  userAgent?: string | undefined;
  details?: Record<string, any> | undefined;
  previousValue?: any;
  newValue?: any;
  duration?: number | undefined;
}

export interface UserActionContext {
  component?: string;
  page?: string;
  feature?: string;
  metadata?: Record<string, any>;
}

export class UserActionLogger {
  private isInitialized = false;
  private batchQueue: UserActionLogDto[] = [];
  private readonly batchSize = 10;
  // private readonly batchTimeout = 5000; // 5 seconds - currently unused
  private batchTimer?: NodeJS.Timeout;
  private isOnline = true;

  constructor() {
    this.setupNetworkListeners();
  }

  /**
   * Initialize the user action logger
   */
  initialize(): void {
    if (this.isInitialized) return;

    console.log('UserActionLogger: Initializing...');
    this.isInitialized = true;
    this.startBatchTimer();

    // Process any queued actions from offline mode
    this.processBatchQueue();
  }

  /**
   * Log a user action with enhanced error handling, circuit breaker, and endpoint availability checks
   */
  async logUserAction(
    action: UserAction,
    context?: UserActionContext
  ): Promise<void> {
    try {
      // Only log if initialized to prevent spam
      if (!this.isInitialized) {
        console.warn('UserActionLogger: Not initialized, skipping log');
        return;
      }

      // Check circuit breaker - skip logging if service is down
      if (this.isCircuitBreakerOpen()) {
        console.warn('UserActionLogger: Circuit breaker open, skipping log to prevent further failures');
        return;
      }

      // Check endpoint availability before attempting to log
      const serviceHealth = loggingHealthService.getServiceHealth('userActionLogger');
      if (!serviceHealth.endpointAvailable && !serviceHealth.isHealthy) {
        console.warn('UserActionLogger: Endpoint unavailable, storing for offline processing');
        const userActionLog = this.createUserActionLog(action, context);
        this.storeOfflineAction(userActionLog);
        return;
      }

      const userActionLog = this.createUserActionLog(action, context);

      if (this.isOnline) {
        // Try immediate logging for critical actions
        if (this.isCriticalAction(action.type)) {
          await this.sendUserActionLog(userActionLog);
        } else {
          // Add to batch queue for non-critical actions
          this.addToBatch(userActionLog);
        }
      } else {
        // Store offline for later processing
        this.storeOfflineAction(userActionLog);
      }
    } catch (error) {
      console.error('UserActionLogger: Failed to log user action:', error);
      // Don't throw - logging failures shouldn't break user experience
      // This ensures truly non-blocking behavior
    }
  }

  /**
   * Log user login action
   */
  async logLogin(
    userId: string,
    loginMethod: string = 'email',
    additionalData?: Record<string, any>
  ): Promise<void> {
    await this.logUserAction({
      type: 'login',
      resourceType: 'authentication',
      userId,
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        loginMethod,
        timestamp: new Date().toISOString(),
        success: true,
        ...additionalData
      }
    }, {
      component: 'AuthService',
      feature: 'authentication',
      metadata: {
        url: window.location.href,
        httpMethod: 'POST',
        statusCode: 200
      }
    });
  }

  /**
   * Log user logout action
   */
  async logLogout(
    userId: string,
    logoutReason: string = 'user_initiated',
    sessionDuration?: number
  ): Promise<void> {
    await this.logUserAction({
      type: 'logout',
      resourceType: 'authentication',
      userId,
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      duration: sessionDuration,
      details: {
        logoutReason,
        timestamp: new Date().toISOString()
      }
    }, {
      component: 'AuthService',
      feature: 'authentication'
    });
  }

  /**
   * Log failed login attempt with wrong password
   */
  async logFailedLogin(
    email: string,
    failureReason: 'wrong_password' | 'user_not_found' | 'account_locked' | 'invalid_credentials' = 'wrong_password',
    additionalData?: Record<string, any>
  ): Promise<void> {
    await this.logUserAction({
      type: 'login_failed',
      resourceType: 'authentication',
      userId: undefined, // No user ID for failed attempts
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        email,
        failureReason,
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    }, {
      component: 'AuthService',
      feature: 'authentication'
    });
  }

  /**
   * Log user registration/signup action
   */
  async logSignup(
    userId: string,
    email: string,
    registrationMethod: string = 'email',
    additionalData?: Record<string, any>
  ): Promise<void> {
    await this.logUserAction({
      type: 'signup',
      resourceType: 'authentication',
      userId,
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        email,
        registrationMethod,
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    }, {
      component: 'AuthService',
      feature: 'registration'
    });
  }

  /**
   * Log failed signup attempt
   */
  async logFailedSignup(
    email: string,
    failureReason: string,
    validationErrors?: string[],
    additionalData?: Record<string, any>
  ): Promise<void> {
    await this.logUserAction({
      type: 'signup_failed',
      resourceType: 'authentication',
      userId: undefined,
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        email,
        failureReason,
        validationErrors,
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    }, {
      component: 'AuthService',
      feature: 'registration'
    });
  }

  /**
   * Log CRUD operations
   */
  async logCrudOperation(
    operation: 'create' | 'read' | 'update' | 'delete',
    resourceType: string,
    resourceId?: string,
    previousValue?: any,
    newValue?: any,
    context?: UserActionContext
  ): Promise<void> {
    await this.logUserAction({
      type: `${operation}_${resourceType}`,
      resourceType,
      resourceId,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      previousValue,
      newValue,
      details: {
        operation,
        timestamp: new Date().toISOString()
      }
    }, context);
  }

  /**
   * Log navigation actions
   */
  async logNavigation(
    fromPage: string,
    toPage: string,
    navigationMethod: string = 'click'
  ): Promise<void> {
    await this.logUserAction({
      type: 'navigation',
      resourceType: 'page',
      resourceId: toPage,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        fromPage,
        toPage,
        navigationMethod,
        timestamp: new Date().toISOString()
      }
    }, {
      component: 'Router',
      feature: 'navigation'
    });
  }

  /**
   * Log search actions
   */
  async logSearch(
    searchTerm: string,
    searchType: string,
    resultsCount?: number,
    filters?: Record<string, any>
  ): Promise<void> {
    await this.logUserAction({
      type: 'search',
      resourceType: 'search_query',
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        searchTerm,
        searchType,
        resultsCount,
        filters,
        timestamp: new Date().toISOString()
      }
    }, {
      component: 'SearchService',
      feature: 'search'
    });
  }

  /**
   * Log form submissions
   */
  async logFormSubmission(
    formName: string,
    formData: Record<string, any>,
    success: boolean,
    validationErrors?: string[]
  ): Promise<void> {
    await this.logUserAction({
      type: 'form_submission',
      resourceType: 'form',
      resourceId: formName,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        formName,
        success,
        validationErrors,
        fieldCount: Object.keys(formData).length,
        timestamp: new Date().toISOString()
      }
    }, {
      component: 'FormHandler',
      feature: 'form_processing'
    });
  }

  /**
   * Log file operations
   */
  async logFileOperation(
    operation: 'upload' | 'download' | 'delete',
    fileName: string,
    fileSize?: number,
    fileType?: string
  ): Promise<void> {
    await this.logUserAction({
      type: `file_${operation}`,
      resourceType: 'file',
      resourceId: fileName,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      details: {
        operation,
        fileName,
        fileSize,
        fileType,
        timestamp: new Date().toISOString()
      }
    }, {
      component: 'FileService',
      feature: 'file_management'
    });
  }

  /**
   * Create user action log DTO
   */
  private createUserActionLog(
    action: UserAction,
    context?: UserActionContext
  ): UserActionLogDto {
    const dto: UserActionLogDto = {
      id: this.generateLogId(),
      timestamp: new Date().toISOString(),
      level: 'info',
      message: `User action: ${action.type}`,
      source: 'UserActionLogger',
      category: 'user_action',
      action: action.type,
      resourceType: action.resourceType,
      sessionId: action.sessionId || this.getSessionId(),
      tags: this.generateTags(action, context),
      metadata: {
        ...action.details,
        context: context || {},
        url: window.location.href,
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      }
    };

    // Only add optional fields if they have values
    const userId = action.userId || this.getCurrentUserId();
    if (userId) dto.userId = userId;
    if (action.ipAddress) dto.ipAddress = action.ipAddress;
    if (action.userAgent || navigator.userAgent) dto.userAgent = action.userAgent || navigator.userAgent;
    if (action.resourceId) dto.resourceId = action.resourceId;
    if (action.previousValue !== undefined) dto.previousValue = action.previousValue;
    if (action.newValue !== undefined) dto.newValue = action.newValue;
    if (action.duration !== undefined) dto.duration = action.duration;

    return dto;
  }

  /**
   * Send user action log to API with comprehensive error handling
   */
  private async sendUserActionLog(userActionLog: UserActionLogDto): Promise<void> {
    try {
      // Map the DTO to match API expectations
      const apiPayload = this.mapToApiFormat(userActionLog);

      // Capture payload for diagnostic purposes
      UserActionLoggerDiagnostic.capturePayload(userActionLog);

      // Validate payload before sending
      const validation = UserActionLoggerDiagnostic.validatePayload(userActionLog);
      if (!validation.isValid) {
        console.warn('UserActionLogger: Payload validation failed:', validation);
        // Still try to send, but log the issues
      }

      await apiService.post('/api/LogManagement/user-actions', apiPayload);
      console.log('✅ User action logged:', userActionLog.action);
      
      // Reset circuit breaker on successful request
      this.recordSuccessfulLogging();
    } catch (error: any) {
      // Comprehensive error handling with graceful degradation
      this.handleLoggingError(error, userActionLog);
      
      // NEVER throw error to prevent breaking main application flow
      // This ensures truly non-blocking behavior as per requirements
    }
  }

  /**
   * Handle logging errors with comprehensive graceful degradation
   */
  private handleLoggingError(error: any, userActionLog: UserActionLogDto): void {
    const errorInfo = {
      action: userActionLog.action,
      error: error?.message || 'Unknown error',
      statusCode: error?.response?.status,
      responseData: error?.response?.data,
      timestamp: new Date().toISOString(),
      url: error?.config?.url || '/api/LogManagement/user-actions'
    };

    // Enhanced console logging for debugging without throwing exceptions
    console.error('UserActionLogger: Failed to send user action log:', errorInfo);
    
    // Log detailed error information for debugging
    if (error?.response?.data) {
      console.error('UserActionLogger: Backend response details:', error.response.data);
    }
    
    if (error?.config) {
      console.error('UserActionLogger: Request configuration:', {
        method: error.config.method,
        url: error.config.url,
        headers: error.config.headers
      });
    }

    // Capture the validation error for analysis
    UserActionLoggerDiagnostic.captureValidationError(error, userActionLog);

    // Determine if we should retry based on error type
    const shouldRetry = this.shouldRetryLogging(error);
    
    if (shouldRetry) {
      // Store for retry if it's a network issue or server error
      this.storeOfflineAction(userActionLog);
      console.log('UserActionLogger: Stored action for retry due to recoverable error');
    } else {
      // Log validation errors or client errors for debugging but don't retry
      console.warn('UserActionLogger: Not retrying due to client error (likely validation issue)');
      this.storeFailedAction(userActionLog, error);
    }

    // Check if logging service is consistently failing
    this.checkLoggingServiceHealth(error);
    
    // Ensure main application flow continues regardless of logging failures
    // This is critical for non-blocking behavior
  }

  /**
   * Determine if logging should be retried based on error type
   */
  private shouldRetryLogging(error: any): boolean {
    const statusCode = error?.response?.status;
    
    // Don't retry client errors (400-499) except for 408 (timeout) and 429 (rate limit)
    if (statusCode >= 400 && statusCode < 500) {
      return statusCode === 408 || statusCode === 429;
    }
    
    // Retry server errors (500+)
    if (statusCode >= 500) {
      return true;
    }
    
    // Retry network errors
    if (error?.name === 'NetworkError' || error?.message?.includes('fetch')) {
      return true;
    }
    
    // Retry timeout errors
    if (error?.name === 'AbortError' || error?.message?.includes('timeout')) {
      return true;
    }
    
    return false;
  }

  /**
   * Store failed actions for debugging (non-retryable errors)
   */
  private storeFailedAction(userActionLog: UserActionLogDto, error: any): void {
    try {
      const failedActions = this.getFailedActions();
      failedActions.push({
        action: userActionLog,
        error: {
          message: error?.message,
          statusCode: error?.response?.status,
          responseData: error?.response?.data
        },
        timestamp: new Date().toISOString()
      });

      // Keep only the most recent 25 failed actions
      if (failedActions.length > 25) {
        failedActions.splice(0, failedActions.length - 25);
      }

      localStorage.setItem('failedUserActions', JSON.stringify(failedActions));
    } catch (storageError) {
      console.error('UserActionLogger: Failed to store failed action:', storageError);
    }
  }

  /**
   * Get failed actions from storage
   */
  private getFailedActions(): any[] {
    try {
      return JSON.parse(localStorage.getItem('failedUserActions') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Record successful logging to reset circuit breaker
   */
  private recordSuccessfulLogging(): void {
    const healthKey = 'userActionLoggerHealth';
    
    try {
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      // Reset failure count on successful request
      if (healthData.failures > 0) {
        healthData.failures = Math.max(0, healthData.failures - 1);
        healthData.lastSuccess = Date.now();
        
        // Close circuit breaker if it was open and we have a success
        if (healthData.circuitOpen) {
          healthData.circuitOpen = false;
          console.log('UserActionLogger: Circuit breaker closed after successful request');
        }
        
        localStorage.setItem(healthKey, JSON.stringify(healthData));
      }
    } catch (storageError) {
      console.error('UserActionLogger: Failed to record successful logging:', storageError);
    }
  }

  /**
   * Check logging service health and implement enhanced circuit breaker pattern
   */
  private checkLoggingServiceHealth(error: any): void {
    const healthKey = 'userActionLoggerHealth';
    const now = Date.now();
    
    try {
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      // Initialize health data if not exists
      if (!healthData.failures) {
        healthData.failures = 0;
        healthData.lastFailure = now;
        healthData.circuitOpen = false;
        healthData.consecutiveFailures = 0;
      }
      
      // Increment failure counts
      healthData.failures++;
      healthData.consecutiveFailures = (healthData.consecutiveFailures || 0) + 1;
      healthData.lastFailure = now;
      
      // Enhanced circuit breaker logic
      const shouldOpenCircuit = 
        healthData.consecutiveFailures >= 5 || // 5 consecutive failures
        (healthData.failures >= 10 && (now - (healthData.lastSuccess || 0)) > 60000); // 10 failures in 1 minute
      
      if (shouldOpenCircuit && !healthData.circuitOpen) {
        healthData.circuitOpen = true;
        healthData.circuitOpenedAt = now;
        console.warn('UserActionLogger: Circuit breaker opened due to repeated failures', {
          consecutiveFailures: healthData.consecutiveFailures,
          totalFailures: healthData.failures,
          errorType: error?.response?.status || 'network'
        });
      }
      
      // Auto-reset circuit breaker after 5 minutes with exponential backoff
      const backoffTime = Math.min(300000, 60000 * Math.pow(2, Math.floor(healthData.failures / 5))); // Max 5 minutes
      if (healthData.circuitOpen && (now - healthData.circuitOpenedAt) > backoffTime) {
        healthData.circuitOpen = false;
        healthData.consecutiveFailures = 0;
        console.log('UserActionLogger: Circuit breaker reset after timeout', {
          backoffTime: backoffTime / 1000 + 's'
        });
      }
      
      localStorage.setItem(healthKey, JSON.stringify(healthData));
    } catch (storageError) {
      console.error('UserActionLogger: Failed to update health data:', storageError);
      // Ensure main application flow continues even if health tracking fails
    }
  }

  /**
   * Check if circuit breaker is open
   */
  private isCircuitBreakerOpen(): boolean {
    try {
      const healthData = JSON.parse(localStorage.getItem('userActionLoggerHealth') || '{}');
      return healthData.circuitOpen || false;
    } catch {
      return false;
    }
  }

  /**
   * Generate meaningful description from action details
   */
  private generateDescription(userActionLog: UserActionLogDto): string {
    // If message is provided, use it
    if (userActionLog.message) {
      return userActionLog.message;
    }

    const action = userActionLog.action || 'unknown_action';
    const details = userActionLog.metadata || {};
    const previousValue = userActionLog.previousValue;
    const newValue = userActionLog.newValue;

    // Generate descriptions based on action type
    switch (action) {
      case 'update_user':
      case 'edit_user': {
        const targetEmail = details.targetUserEmail || newValue?.email || previousValue?.email || 'user';
        const fieldsChanged = details.fieldsChanged || details.updatedFields || [];
        if (fieldsChanged.length > 0) {
          return `Edited user ${targetEmail} (changed: ${fieldsChanged.join(', ')})`;
        }
        return `Edited user ${targetEmail}`;
      }
      case 'create_user': {
        const userEmail = newValue?.email || details.createdUserEmail || 'user';
        return `Created user ${userEmail}`;
      }
      case 'delete_user': {
        const userEmail = previousValue?.email || details.deletedUserEmail || 'user';
        return `Deleted user ${userEmail}`;
      }
      case 'toggle_user_status':
      case 'activate_user':
      case 'deactivate_user': {
        const userEmail = newValue?.email || previousValue?.email || details.targetUserEmail || 'user';
        const isActive = newValue?.isActive ?? details.newStatus ?? false;
        return `${isActive ? 'Activated' : 'Deactivated'} user ${userEmail}`;
      }
      case 'assign_role':
      case 'remove_role': {
        const userEmail = newValue?.email || previousValue?.email || 'user';
        const roleName = details.roleName || details.role || 'role';
        return `${action === 'assign_role' ? 'Assigned' : 'Removed'} role ${roleName} to user ${userEmail}`;
      }
      case 'login':
        return `User logged in`;
      case 'logout':
        return `User logged out`;
      case 'signup':
        return `New user registered: ${details.email || 'user'}`;
      default:
        // Try to generate description from details
        if (details.description) {
          return details.description;
        }
        return `User action: ${action}`;
    }
  }

  /**
   * Map UserActionLogDto to API format matching CreateUserActionLogDto schema
   */
  private mapToApiFormat(userActionLog: UserActionLogDto): CreateUserActionLogDto {
    // Ensure required fields are present and valid according to swagger schema
    const userId = (userActionLog.userId || this.getCurrentUserId() || 'anonymous').substring(0, 50);
    const action = (userActionLog.action || 'unknown_action').substring(0, 100);
    const category = (userActionLog.category || 'user_action').substring(0, 100);
    const resource = (userActionLog.resourceType || 'unknown_resource').substring(0, 200);
    const description = this.generateDescription(userActionLog);

    // Build payload matching CreateUserActionLogDto schema exactly
    const apiPayload: any = {
      // Required fields (must be present)
      userId,
      action,
      category,
      resource,
      description,
      
      // Optional fields (only include if they have values)
      resourceId: userActionLog.resourceId || undefined,
      oldValues: userActionLog.previousValue !== undefined ? JSON.stringify(userActionLog.previousValue) : undefined,
      newValues: userActionLog.newValue !== undefined ? JSON.stringify(userActionLog.newValue) : undefined,
      additionalData: userActionLog.metadata ? JSON.stringify(userActionLog.metadata) : undefined,
      ipAddress: userActionLog.ipAddress || undefined,
      userAgent: userActionLog.userAgent || navigator.userAgent || undefined,
      requestPath: userActionLog.metadata?.url || window.location.pathname || undefined,
      httpMethod: userActionLog.metadata?.httpMethod || 'GET',
      traceId: userActionLog.traceId || undefined,
      responseTimeMs: userActionLog.duration || undefined,
      statusCode: userActionLog.metadata?.statusCode || undefined,
      isSuccess: !userActionLog.level || userActionLog.level !== 'error',
      errorMessage: userActionLog.level === 'error' ? userActionLog.message : undefined
    };

    // Remove undefined values to keep payload clean
    Object.keys(apiPayload).forEach(key => {
      if (apiPayload[key] === undefined) {
        delete apiPayload[key];
      }
    });

    return apiPayload;
  }

  /**
   * Add action to batch queue
   */
  private addToBatch(userActionLog: UserActionLogDto): void {
    this.batchQueue.push(userActionLog);

    if (this.batchQueue.length >= this.batchSize) {
      this.processBatchQueue();
    }
  }

  /**
   * Process batch queue with enhanced error handling
   */
  private async processBatchQueue(): Promise<void> {
    if (this.batchQueue.length === 0) return;

    // Check circuit breaker before processing batch
    if (this.isCircuitBreakerOpen()) {
      console.warn('UserActionLogger: Circuit breaker open, skipping batch processing');
      return;
    }

    const batch = [...this.batchQueue];
    this.batchQueue = [];

    let successCount = 0;
    let failureCount = 0;

    // Process each item individually to avoid losing entire batch on single failure
    for (const userActionLog of batch) {
      try {
        await this.sendUserActionLog(userActionLog);
        successCount++;
      } catch (error) {
        failureCount++;
        // Individual items handle their own error logging and storage
        // Don't re-queue here as sendUserActionLog handles retry logic
      }
    }

    if (successCount > 0) {
      console.log(`UserActionLogger: Successfully processed ${successCount} of ${batch.length} actions in batch`);
    }
    
    if (failureCount > 0) {
      console.warn(`UserActionLogger: Failed to process ${failureCount} of ${batch.length} actions in batch`);
    }
  }

  /**
   * Start batch timer
   */
  private startBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    // Temporarily disable batch timer to prevent spam
    // this.batchTimer = setInterval(() => {
    //   if (this.batchQueue.length > 0) {
    //     this.processBatchQueue();
    //   }
    // }, this.batchTimeout);
  }

  /**
   * Store action offline for later processing
   */
  private storeOfflineAction(userActionLog: UserActionLogDto): void {
    try {
      const offlineActions = this.getOfflineActions();
      offlineActions.push(userActionLog);

      // Keep only the most recent 100 offline actions
      if (offlineActions.length > 100) {
        offlineActions.splice(0, offlineActions.length - 100);
      }

      localStorage.setItem('offlineUserActions', JSON.stringify(offlineActions));
    } catch (error) {
      console.error('UserActionLogger: Failed to store offline action:', error);
    }
  }

  /**
   * Get offline actions from storage
   */
  private getOfflineActions(): UserActionLogDto[] {
    try {
      return JSON.parse(localStorage.getItem('offlineUserActions') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Process offline actions when back online
   */
  private async processOfflineActions(): Promise<void> {
    const offlineActions = this.getOfflineActions();
    if (offlineActions.length === 0) return;

    console.log(`UserActionLogger: Processing ${offlineActions.length} offline actions`);

    try {
      for (const action of offlineActions) {
        await this.sendUserActionLog(action);
      }

      // Clear offline storage after successful processing
      localStorage.removeItem('offlineUserActions');
      console.log('UserActionLogger: Successfully processed all offline actions');
    } catch (error) {
      console.error('UserActionLogger: Failed to process offline actions:', error);
    }
  }

  /**
   * Setup network listeners
   */
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      console.log('UserActionLogger: Network back online, processing offline actions');
      this.isOnline = true;
      this.processOfflineActions();
    });

    window.addEventListener('offline', () => {
      console.log('UserActionLogger: Network offline, switching to offline mode');
      this.isOnline = false;
    });

    this.isOnline = navigator.onLine;
  }

  /**
   * Check if action is critical and should be logged immediately
   */
  private isCriticalAction(actionType: string): boolean {
    const criticalActions = [
      'login',
      'logout',
      'delete_user',
      'delete_content',
      'delete_error-log',  // Make error log deletes critical
      'update_error-log',  // Make error log updates critical
      'security_violation',
      'admin_action'
    ];
    return criticalActions.includes(actionType);
  }

  /**
   * Generate tags for the action
   */
  private generateTags(action: UserAction, context?: UserActionContext): string[] {
    const tags = ['user-action', action.type, action.resourceType];

    if (context?.component) tags.push(`component:${context.component}`);
    if (context?.feature) tags.push(`feature:${context.feature}`);
    if (action.userId) tags.push(`user:${action.userId}`);

    return tags;
  }

  /**
   * Generate unique log ID
   */
  private generateLogId(): string {
    return `user_action_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get current user ID
   */
  private getCurrentUserId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('userProfile') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Get session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Get client IP address (best effort)
   */
  private async getClientIP(): Promise<string> {
    try {
      // In a real application, you might get this from a service or header
      // For now, return a placeholder
      return 'client-ip';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Reset circuit breaker manually
   */
  resetCircuitBreaker(): void {
    try {
      const healthData = {
        failures: 0,
        circuitOpen: false,
        lastReset: Date.now()
      };
      localStorage.setItem('userActionLoggerHealth', JSON.stringify(healthData));
      console.log('UserActionLogger: Circuit breaker manually reset');
    } catch (error) {
      console.error('UserActionLogger: Failed to reset circuit breaker:', error);
    }
  }

  /**
   * Get logging service health status
   */
  getHealthStatus(): {
    isHealthy: boolean;
    circuitOpen: boolean;
    failures: number;
    lastFailure?: number;
    batchQueueSize: number;
    offlineActionsCount: number;
    failedActionsCount: number;
  } {
    try {
      const healthData = JSON.parse(localStorage.getItem('userActionLoggerHealth') || '{}');
      const offlineActions = this.getOfflineActions();
      const failedActions = this.getFailedActions();
      
      return {
        isHealthy: !healthData.circuitOpen && (healthData.failures || 0) < 3,
        circuitOpen: healthData.circuitOpen || false,
        failures: healthData.failures || 0,
        lastFailure: healthData.lastFailure,
        batchQueueSize: this.batchQueue.length,
        offlineActionsCount: offlineActions.length,
        failedActionsCount: failedActions.length
      };
    } catch {
      return {
        isHealthy: true,
        circuitOpen: false,
        failures: 0,
        batchQueueSize: this.batchQueue.length,
        offlineActionsCount: 0,
        failedActionsCount: 0
      };
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    // Process any remaining batch items
    if (this.batchQueue.length > 0) {
      this.processBatchQueue();
    }
  }
}

// Export singleton instance
export const userActionLogger = new UserActionLogger();

export default UserActionLogger;